import Form from 'carbon-react/lib/components/form';
import Message from 'carbon-react/lib/components/message';
import { MessageVariant } from 'carbon-react/lib/components/message/message.component';
import { useFormik } from 'formik';
import I18n from 'i18n-js';
import styled from 'styled-components';
import PublicButton from '../../components/Shared/Public/PublicButton';
import PublicForm from '../../components/Shared/Public/PublicForm';
import PublicTextBox from '../../components/Shared/Public/PublicTextBox';

const ErrorWrapper = styled.div`
  margin-top: 20px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
`;

export interface ForgotPasswordFormValues {
  username: string;
  company: string;
}

export interface ForgotPasswordFormProps {
  loading: boolean | undefined;
  initialValues?: ForgotPasswordFormValues;
  formMessage?: {
    variant: MessageVariant;
    message: string;
  };
  onSubmit: (values: ForgotPasswordFormValues) => void;
  onCancel: () => void;
  onCompanyBlur: (companyId: string) => void;
}

const INITIAL_VALUES: ForgotPasswordFormValues = {
  username: '',
  company: '',
};

const validate = (values: ForgotPasswordFormValues) => {
  const errors: Partial<ForgotPasswordFormValues> = {};

  if (!values.username) {
    errors.username = I18n.t('common.Required');
  }

  if (!values.company) {
    errors.company = I18n.t('common.Required');
  }

  return errors;
};

const ResetPasswordForm = ({
  loading,
  formMessage,
  onSubmit,
  onCancel,
  initialValues = INITIAL_VALUES,
  onCompanyBlur,
}: ForgotPasswordFormProps) => {
  const { handleSubmit, values, errors, handleChange } = useFormik<ForgotPasswordFormValues>({
    initialValues,
    validate,
    isInitialValid: false,
    validateOnChange: false,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { company, username } = values;

  return (
    <PublicForm title="auth.ForgotYourPassword" subTitle="auth.Entertheemailasociatedwith" subTitleMarkdown={true}>
      <Form onSubmit={handleSubmit}>
        <PublicTextBox
          id="company"
          name="company"
          label={I18n.t('auth.IntacctCompanyID')}
          value={company}
          onChange={handleChange}
          onBlur={(ev) => onCompanyBlur(ev.currentTarget.value)}
          error={errors?.company}
        />
        <PublicTextBox
          id="username"
          name="username"
          label={I18n.t('auth.Username')}
          value={username}
          onChange={handleChange}
          error={errors?.username}
        />

        {formMessage && (
          <ErrorWrapper>
            <Message variant={formMessage.variant}>{formMessage.message}</Message>
          </ErrorWrapper>
        )}
        <Actions>
          <PublicButton buttonType="secondary" label="Cancel" onClick={onCancel} />
          <PublicButton buttonType="primary" type="submit" loading={loading} label="Submit" />
        </Actions>
      </Form>
    </PublicForm>
  );
};

export default ResetPasswordForm;
